import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, first, map } from 'rxjs/operators';
import { of } from 'rxjs';

import { AppState } from '../../store';
import { TenantsDBService } from '../../services/tenants-db.service';
import { setSessionLanguage, themeChanged } from '../../store/platform.actions';
import { checkIfSingleSignOnPage, DEFAULT_THEME } from '../../common/platform-utils';

/**
 * Applies the school's branding and language before the reset-link page paints.
 *
 * Modelled on LoginGuard, with one deliberate difference: it always lets the page through.
 * LoginGuard returns `!!tenant`, which is right for a login page but wrong here - this page is
 * the last thing standing between a student and a dead end, so a tenant we cannot look up should
 * cost them the school's colours, never the page itself.
 */
export const authActionBrandGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const store = inject<Store<AppState>>(Store);
  const tenantsDB = inject(TenantsDBService);

  // On a school's own domain the tenant is already resolved from the hostname.
  if (!checkIfSingleSignOnPage()) {
    return true;
  }

  const tenantId = route.queryParamMap.get('tenantId');
  if (!tenantId) {
    store.dispatch(themeChanged(DEFAULT_THEME));
    return true;
  }

  return tenantsDB.findTenantById(tenantId).pipe(
    first(),
    map(tenant => {
      if (tenant) {
        store.dispatch(themeChanged(tenant.brandTheme));
        store.dispatch(
          setSessionLanguage({ language: tenant?.languageConfig?.siteLanguage ?? 'en' })
        );
      } else {
        store.dispatch(themeChanged(DEFAULT_THEME));
      }
      return true;
    }),
    catchError(() => of(true))
  );
};
